import React, { useContext, useMemo } from 'react';
import { Table as BaseTable } from '@ubeya/shared-web/components';
import ConfigContext from '@ubeya/shared-web/contexts/ConfigContext';
import styled, { css } from 'styled-components';
import { isSafari } from 'react-device-detect';
import useFormattingMap from '@ubeya/shared-web/hooks/useFormattingMap';
import { CONTRACT_TYPES_WITH_MONEY_POT_BALANCE } from '@ubeya/shared/constants';

const Table = styled(BaseTable)`
  ${({ $isSafari }) =>
    $isSafari
      ? css`
          max-width: 90%;
        `
      : css`
          width: 100%;
        `}

  .row.highlight {
    background: ${({ theme }) => theme.colors.grey1} !important;
    &.positive {
      background: ${({ theme }) => theme.colors.greenExtraLight} !important;
    }
    &.negative {
      background: ${({ theme }) => theme.colors.redExtraLight} !important;
    }
  }

  .row.section-header {
    background: ${({ theme }) => theme.colors.grey1} !important;
    font-weight: 600;
  }

  .row:hover:not(.highlight):not(.section-header) {
    background: none !important;
    svg {
      visibility: visible;
    }
  }
`;

const Amount = styled.p`
  font-weight: ${({ isBold }) => (isBold ? '600' : 'normal')};
  color: ${({ theme, isHighlight, amount = 0 }) => {
    if (!isHighlight || amount === 0) return theme.colors.black;
    if (amount > 0) return theme.colors.greenPositive;
    return theme.colors.redNegative;
  }};
`;

const EmployeeBalance = ({ balance, activeContract }) => {
  const { t } = useContext(ConfigContext);
  const formattingMap = useFormattingMap();

  const isContractTypeWithMoneyPotBalance = useMemo(() => {
    if (!activeContract?.accountContract?.type) return false;
    return CONTRACT_TYPES_WITH_MONEY_POT_BALANCE.includes(activeContract.accountContract.type);
  }, [activeContract?.accountContract?.type]);

  const tableData = useMemo(() => {
    if (!balance) return [];
    const {
      holidayAccrualBalance,
      holidayAccrualEntitlement,
      holidayAllowance,
      holidayDaysUsed,
      holidayDaysRemaining,
      holidayDaysAccrued,
      holidayBalanceAdjustments,
      sickLeaveAccrualEntitlement,
      sickDaysAllowance,
      sickDaysUsed,
      sickDaysRemaining,
      toilHoursBalance,
      toilBalanceAdjustments
    } = balance;

    const rows = [];

    // Holiday section — allowance / used / remaining. For workdays-accrual contracts
    // (Mini/Midi DE) the allowance is the days-actually-earned floor, otherwise the
    // pro-rated yearly entitlement.
    rows.push({ title: t('contractTypes_holidayBalanceSection'), isSectionHeader: true });
    const isWorkdaysAccrualContract = holidayDaysAccrued !== null && holidayDaysAccrued !== undefined;
    rows.push({
      title: t('contractTypes_holidayAllowance_withEntitlement', { entitlement: holidayAccrualEntitlement }),
      amount: isWorkdaysAccrualContract ? holidayDaysAccrued : holidayAllowance,
      unit: 'days'
    });
    (holidayBalanceAdjustments || []).forEach((adjustment) => {
      rows.push({
        date: adjustment.createdAt,
        adminName: adjustment.adminName,
        title: t('holidayBalanceAdjustment'),
        amount: adjustment.amount,
        reason: adjustment.reason,
        unit: 'days'
      });
    });
    rows.push({
      title: t('contractTypes_holidayUsed'),
      amount: holidayDaysUsed,
      unit: 'days'
    });
    rows.push({
      title: isContractTypeWithMoneyPotBalance
        ? t('contractTypes_holidayRemaining_withBalance', {
            balance: formattingMap.currency(holidayAccrualBalance || 0, { includeZero: true })
          })
        : t('contractTypes_holidayRemaining'),
      amount: holidayDaysRemaining,
      unit: 'days',
      isHighlight: true
    });

    // Sick section — allowance / used / remaining. Same shape as Holiday for visual consistency.
    if (sickLeaveAccrualEntitlement || sickDaysAllowance || sickDaysUsed || sickDaysRemaining !== undefined) {
      rows.push({ title: t('contractTypes_sickBalanceSection'), isSectionHeader: true });
      rows.push({
        title: t('contractTypes_sickAllowance_withEntitlement', { entitlement: sickLeaveAccrualEntitlement }),
        amount: sickDaysAllowance,
        unit: 'days'
      });
      rows.push({
        title: t('contractTypes_sickUsed'),
        amount: sickDaysUsed,
        unit: 'days'
      });
      rows.push({
        title: t('contractTypes_sickRemaining'),
        amount: sickDaysRemaining,
        unit: 'days',
        isHighlight: true
      });
    }

    // TOIL section — accrued / added / used / available. Only for contracts with TOIL accrual.
    if (toilHoursBalance) {
      rows.push({ title: t('contractTypes_toilBalanceSection'), isSectionHeader: true });
      rows.push({
        title: t('contractTypes_toilAccruedHours'),
        amount: toilHoursBalance.accrued,
        unit: 'hours'
      });
      (toilBalanceAdjustments || []).forEach((adjustment) => {
        rows.push({
          date: adjustment.createdAt,
          adminName: adjustment.adminName,
          title: t('toilBalanceAdjustment'),
          amount: adjustment.amount,
          reason: adjustment.reason,
          unit: 'hours'
        });
      });
      rows.push({
        title: t('contractTypes_toilUsedHours'),
        amount: toilHoursBalance.used,
        unit: 'hours'
      });
      rows.push({
        title: t('contractTypes_toilAvailableHours'),
        amount: toilHoursBalance.remaining,
        unit: 'hours',
        isHighlight: true
      });
    }

    return rows;
  }, [balance, formattingMap, isContractTypeWithMoneyPotBalance, t]);

  const columns = useMemo(
    () => [
      { dataKey: 'title', minWidth: 260, flex: 2 },
      {
        dataKey: 'amount',
        minWidth: 110,
        flex: 1,
        Cell: ({ rowData: { amount = null, date, isHighlight, unit = 'days', isSectionHeader } }) => {
          if (isSectionHeader) return '';
          if (amount === null || amount === undefined) return '';

          const unitLabel = unit === 'hours' ? t('hours') : t('days');
          const label = `${amount} ${unitLabel}`;
          return (
            <Amount isBold={!date} isHighlight={isHighlight} amount={amount}>
              {label}
            </Amount>
          );
        }
      },
      {
        dataKey: 'reason',
        minWidth: 140,
        flex: 1
      },
      {
        dataKey: 'adminName',
        minWidth: 140,
        flex: 1
      },
      {
        Header: t('date'),
        dataKey: 'date',
        minWidth: 120,
        flex: 1,
        formatter: (value) => (value ? formattingMap.date(value) : ''),
        sortable: true
      }
    ],
    [formattingMap, t]
  );

  return (
    <Table
      isVirtualized={false}
      autoHeight
      headless
      showCellBorder={false}
      data={tableData}
      rowStyle={({ isHighlight, isSectionHeader, amount }) => {
        if (isSectionHeader) return 'section-header';
        if (isHighlight) {
          if (amount > 0) return 'highlight positive';
          if (amount < 0) return 'highlight negative';
          return 'highlight';
        }
        return '';
      }}
      columns={columns}
      $isSafari={isSafari}
    />
  );
};

export default EmployeeBalance;
